import * as THREE from 'three';
import {
  CRAFT_BLOCK_TYPE,
  MAX_BATCH_EDITS,
  currentWorldAnimTick,
  customBlockId,
  libraryBlockId,
  mirrorBlockTransform,
  rotateBlockTransform,
  type BlockTransformAxis,
  type BlockEditTuple,
} from '@shared/protocol';
import { World } from '../world/World';
import { raycastVoxels, VoxelHit } from '../world/raycast';
import {
  BLOCK_AIR,
  WORLD_SIZE_X,
  WORLD_SIZE_Z,
  WORLD_HEIGHT,
  WORLD_MIN_Y,
} from '../world/constants';
import {
  PlayerController,
  PLAYER_HALF_WIDTH,
  PLAYER_HEIGHT,
} from '../player/PlayerController';
import { Hotbar } from './Hotbar';
import { showToast } from '../ui/toast';
import { createFaceOutlineGeometry, orientFaceOutline } from './FaceHighlight';

const REACH = 10;
const BREAK_SECONDS = 1;
const HISTORY_LIMIT = 50;
const FACE_HIGHLIGHT_SIZE = 0.92;
const FACE_OFFSET = 0.502;

interface CellChange {
  x: number;
  y: number;
  z: number;
  before: number;
  after: number;
  beforeTransform: number;
  afterTransform: number;
  beforeViewId: number;
  afterViewId: number;
  beforeAnimOrigin: number;
  afterAnimOrigin: number;
}

interface BuildAction {
  changes: CellChange[];
}

interface Anchor {
  x: number;
  y: number;
  z: number;
  type: number;
  transform: number;
}

export interface BuildEvents {
  onBatch(edits: BlockEditTuple[]): void;
}

/**
 * Minecraft-like block interaction:
 * - hold LMB for one second to break (Ctrl+LMB is immediate);
 * - RMB places;
 * - MMB replaces the aimed block with the selected type;
 * - Alt+RMB builds a hollow cuboid from the last single placement,
 *   or replaces the hollow shell from the last single replace;
 * - local action history provides 50-level undo/redo.
 */
export class BuildingSystem {
  currentHit: VoxelHit | null = null;

  private readonly highlight: THREE.LineSegments;
  private readonly highlightMaterial: THREE.LineBasicMaterial;
  private readonly massPreview: THREE.LineSegments;
  private readonly massPreviewMaterial: THREE.LineBasicMaterial;
  private readonly direction = new THREE.Vector3();
  private readonly isSolid = (x: number, y: number, z: number) =>
    this.world.isSolid(x, y, z);
  private readonly progress = document.getElementById('break-progress')!;
  private readonly progressFill = document.getElementById('break-progress-fill')!;

  private destroyTarget: VoxelHit | null = null;
  private destroyElapsed = 0;
  private anchor: Anchor | null = null;
  private anchorKind: 'place' | 'replace' | null = null;
  private previewTarget: Anchor | null = null;
  private readonly undoStack: BuildAction[] = [];
  private readonly redoStack: BuildAction[] = [];

  constructor(
    private readonly world: World,
    private readonly player: PlayerController,
    private readonly events: BuildEvents,
    scene: THREE.Scene,
  ) {
    this.highlightMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      depthTest: false,
    });
    this.highlight = new THREE.LineSegments(
      createFaceOutlineGeometry(),
      this.highlightMaterial,
    );
    this.highlight.scale.setScalar(FACE_HIGHLIGHT_SIZE);
    this.highlight.renderOrder = 10;
    this.highlight.visible = false;

    this.massPreviewMaterial = new THREE.LineBasicMaterial({
      color: 0x35e6ff,
      depthTest: false,
      transparent: true,
      opacity: 0.9,
    });
    this.massPreview = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)),
      this.massPreviewMaterial,
    );
    this.massPreview.renderOrder = 10;
    this.massPreview.visible = false;
    scene.add(this.highlight, this.massPreview);
  }

  update(
    camera: THREE.PerspectiveCamera,
    active: boolean,
    dt: number,
    primaryHeld: boolean,
    control: boolean,
    massBuild: boolean,
    selectedType: number,
    transformMode = false,
  ): void {
    if (!active) {
      this.currentHit = null;
      this.highlight.visible = false;
      this.massPreview.visible = false;
      this.cancelDestroy();
      return;
    }

    camera.getWorldDirection(this.direction);
    this.currentHit = raycastVoxels(
      this.isSolid,
      camera.position,
      this.direction,
      REACH,
    );
    if (transformMode) {
      this.cancelDestroy();
      this.updateTargetHighlight(false, isTransformableBlock(
        this.world.getBlock(
          this.currentHit?.x ?? -1,
          this.currentHit?.y ?? WORLD_MIN_Y - 1,
          this.currentHit?.z ?? -1,
        ),
      ) ? 0xffb347 : 0x888888);
      this.massPreview.visible = false;
      return;
    }
    this.updateTargetHighlight(control || this.destroyTarget !== null);
    this.updateDestroy(dt, primaryHeld, control);
    this.updateMassPreview(massBuild, selectedType);
  }

  handleClick(
    button: number,
    hotbar: Hotbar,
    control: boolean,
    massBuild: boolean,
  ): void {
    const hit = this.currentHit;
    if (!hit) return;
    if (button === 0) {
      if (control) this.destroyImmediately(hit);
      else this.beginDestroy(hit);
    } else if (button === 1) {
      if (hotbar.selectedBlockId === BLOCK_AIR) {
        showToast('This quick-access slot is empty');
        return;
      }
      this.handleReplace(hit, hotbar.selectedBlockId, massBuild);
    } else if (button === 2) {
      if (hotbar.selectedBlockId === BLOCK_AIR) {
        showToast('This quick-access slot is empty');
        return;
      }
      this.handlePlace(hit, hotbar.selectedBlockId, massBuild);
    }
  }

  transformFocusedBlock(
    axis: BlockTransformAxis,
    mirror: boolean,
    direction: 1 | -1 = 1,
  ): void {
    const hit = this.currentHit;
    if (!hit) {
      showToast('Aim at a custom block first');
      return;
    }
    const type = this.world.getBlock(hit.x, hit.y, hit.z);
    if (!isTransformableBlock(type)) {
      showToast('Only custom and library blocks can be transformed');
      return;
    }
    if (!this.world.canEditAt(hit.x, hit.z)) {
      showToast('You can transform blocks only inside your own zone');
      return;
    }
    const beforeTransform = this.world.getBlockTransform(hit.x, hit.y, hit.z);
    const viewId = this.world.getBlockViewId(hit.x, hit.y, hit.z);
    const animOrigin = this.world.getBlockAnimOrigin(hit.x, hit.y, hit.z);
    const afterTransform = mirror
      ? mirrorBlockTransform(beforeTransform, axis)
      : rotateBlockTransform(beforeTransform, axis, direction);
    this.perform({
      changes: [{
        x: hit.x,
        y: hit.y,
        z: hit.z,
        before: type,
        after: type,
        beforeTransform,
        afterTransform,
        beforeViewId: viewId,
        afterViewId: viewId,
        beforeAnimOrigin: animOrigin,
        afterAnimOrigin: animOrigin,
      }],
    });
  }

  undo(): void {
    const action = this.undoStack.pop();
    if (!action) {
      showToast('Nothing to undo');
      return;
    }
    this.applyAction(action, false);
    this.redoStack.push(action);
    this.clearAnchor();
    this.massPreview.visible = false;
  }

  redo(): void {
    const action = this.redoStack.pop();
    if (!action) {
      showToast('Nothing to redo');
      return;
    }
    this.applyAction(action, true);
    this.undoStack.push(action);
    this.clearAnchor();
    this.massPreview.visible = false;
  }

  clearHistory(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.clearAnchor();
    this.cancelDestroy();
    this.massPreview.visible = false;
  }

  /** Hide world-cell outlines when the closer micro editor owns the ray. */
  suppressVisuals(suppressed: boolean): void {
    if (!suppressed) return;
    this.highlight.visible = false;
    this.massPreview.visible = false;
  }

  cancelDestroy(): void {
    this.destroyTarget = null;
    this.destroyElapsed = 0;
    this.progress.classList.remove('visible');
    this.progressFill.style.width = '0';
    this.highlightMaterial.color.setHex(0xffffff);
    this.highlight.scale.setScalar(FACE_HIGHLIGHT_SIZE);
  }

  private updateTargetHighlight(removeMode: boolean, color?: number): void {
    const hit = this.currentHit;
    if (!hit) {
      this.highlight.visible = false;
      return;
    }
    this.highlightMaterial.color.setHex(color ?? (removeMode ? 0xff5757 : 0xffffff));
    this.highlight.position.set(
      hit.x + 0.5 + hit.nx * FACE_OFFSET,
      hit.y + 0.5 + hit.ny * FACE_OFFSET,
      hit.z + 0.5 + hit.nz * FACE_OFFSET,
    );
    orientFaceOutline(this.highlight, hit.nx, hit.ny, hit.nz);
    this.highlight.scale.setScalar(FACE_HIGHLIGHT_SIZE);
    this.highlight.visible = true;
  }

  private beginDestroy(hit: VoxelHit): void {
    if (!this.world.canEditAt(hit.x, hit.z)) {
      showToast('You can remove blocks only inside your own zone');
      return;
    }
    this.destroyTarget = { ...hit };
    this.destroyElapsed = 0;
    this.progress.classList.add('visible');
  }

  private updateDestroy(dt: number, primaryHeld: boolean, control = false): void {
    if (!this.destroyTarget) return;
    if (!primaryHeld || !sameCell(this.destroyTarget, this.currentHit)) {
      this.cancelDestroy();
      return;
    }
    if (control) {
      const target = this.destroyTarget;
      this.cancelDestroy();
      this.destroyImmediately(target);
      return;
    }

    this.destroyElapsed = Math.min(BREAK_SECONDS, this.destroyElapsed + dt);
    const ratio = this.destroyElapsed / BREAK_SECONDS;
    this.progressFill.style.width = `${Math.round(ratio * 100)}%`;
    this.highlightMaterial.color.setRGB(0.13 + ratio * 0.72, 0.13, 0.13);
    this.highlight.scale.setScalar(
      FACE_HIGHLIGHT_SIZE + Math.sin(ratio * Math.PI * 8) * 0.025,
    );

    if (this.destroyElapsed >= BREAK_SECONDS) {
      const target = this.destroyTarget;
      this.cancelDestroy();
      this.destroyImmediately(target);
    }
  }

  private destroyImmediately(hit: VoxelHit): void {
    this.cancelDestroy();
    if (!this.world.canEditAt(hit.x, hit.z)) {
      showToast('You can remove blocks only inside your own zone');
      return;
    }
    const before = this.world.getBlock(hit.x, hit.y, hit.z);
    if (before === BLOCK_AIR) return;
    const beforeTransform = this.world.getBlockTransform(hit.x, hit.y, hit.z);
    const beforeViewId = this.world.getBlockViewId(hit.x, hit.y, hit.z);
    const beforeAnimOrigin = this.world.getBlockAnimOrigin(hit.x, hit.y, hit.z);
    this.perform({
      changes: [{
        x: hit.x,
        y: hit.y,
        z: hit.z,
        before,
        after: BLOCK_AIR,
        beforeTransform,
        afterTransform: 0,
        beforeViewId,
        afterViewId: 0,
        beforeAnimOrigin,
        afterAnimOrigin: 0,
      }],
    });
  }

  private handleReplace(hit: VoxelHit, type: number, massBuild: boolean): void {
    this.cancelDestroy();
    const target = hitCell(hit, type);
    if (!this.validReplaceTarget(target)) return;

    if (
      massBuild &&
      this.anchor &&
      this.anchorKind === 'replace' &&
      this.anchor.type === type &&
      this.previewTarget &&
      samePosition(target, this.previewTarget)
    ) {
      this.replaceHollowCuboid(this.anchor, target);
      return;
    }

    const before = this.world.getBlock(target.x, target.y, target.z);
    if (before === BLOCK_AIR) return;
    const beforeTransform = this.world.getBlockTransform(target.x, target.y, target.z);
    const beforeViewId = this.world.getBlockViewId(target.x, target.y, target.z);
    const beforeAnimOrigin = this.world.getBlockAnimOrigin(target.x, target.y, target.z);
    const afterViewId = this.world.pickPlacementViewId(type);
    const afterAnimOrigin = currentWorldAnimTick();
    if (
      before !== type ||
      beforeTransform !== 0 ||
      beforeViewId !== afterViewId ||
      beforeAnimOrigin !== afterAnimOrigin
    ) {
      this.perform({
        changes: [{
          ...target,
          before,
          after: type,
          beforeTransform,
          afterTransform: 0,
          beforeViewId,
          afterViewId,
          beforeAnimOrigin,
          afterAnimOrigin,
        }],
      });
    }
    this.anchor = target;
    this.anchorKind = 'replace';
  }

  private handlePlace(hit: VoxelHit, type: number, massBuild: boolean): void {
    if (
      massBuild &&
      this.anchor &&
      this.anchor.type === type &&
      this.previewTarget
    ) {
      if (this.anchorKind === 'replace') {
        const target = hitCell(hit, type);
        if (samePosition(target, this.previewTarget) && this.validReplaceTarget(target)) {
          this.replaceHollowCuboid(this.anchor, target);
        }
        return;
      }
      const target = placementCell(hit, type);
      if (samePosition(target, this.previewTarget) && this.validPlacement(target)) {
        this.buildHollowCuboid(this.anchor, target);
      }
      return;
    }

    const target = placementCell(hit, type);
    if (!this.validPlacement(target)) return;

    if (this.intersectsPlayer(target.x, target.y, target.z)) return;
    const before = this.world.getBlock(target.x, target.y, target.z);
    if (before !== BLOCK_AIR) return;
    this.perform({
      changes: [{
        ...target,
        before,
        after: type,
        beforeTransform: 0,
        afterTransform: target.transform,
        beforeViewId: 0,
        afterViewId: this.world.pickPlacementViewId(type),
        beforeAnimOrigin: 0,
        afterAnimOrigin: currentWorldAnimTick(),
      }],
    });
    this.anchor = target;
    this.anchorKind = 'place';
  }

  private updateMassPreview(massBuild: boolean, selectedType: number): void {
    this.previewTarget = null;
    if (
      !massBuild ||
      !this.anchor ||
      !this.anchorKind ||
      this.anchor.type !== selectedType ||
      !this.currentHit
    ) {
      this.massPreview.visible = false;
      return;
    }

    const replace = this.anchorKind === 'replace';
    const target = replace
      ? hitCell(this.currentHit, selectedType)
      : placementCell(this.currentHit, selectedType);
    const bounds = cuboidBounds(this.anchor, target);
    const valid = replace
      ? this.validReplaceTarget(target, false) &&
        this.world.getBlock(target.x, target.y, target.z) !== BLOCK_AIR &&
        this.world.canEditAt(bounds.minX, bounds.minZ) &&
        this.world.canEditAt(bounds.maxX, bounds.maxZ) &&
        hollowShellCount(bounds) <= MAX_BATCH_EDITS
      : this.validPlacement(target, false) &&
        this.world.canEditAt(bounds.minX, bounds.minZ) &&
        this.world.canEditAt(bounds.maxX, bounds.maxZ) &&
        hollowShellCount(bounds) <= MAX_BATCH_EDITS;

    this.previewTarget = valid ? target : null;
    this.massPreviewMaterial.color.setHex(valid ? 0x35e6ff : 0xff4545);
    this.massPreview.position.set(
      (bounds.minX + bounds.maxX + 1) / 2,
      (bounds.minY + bounds.maxY + 1) / 2,
      (bounds.minZ + bounds.maxZ + 1) / 2,
    );
    this.massPreview.scale.set(
      bounds.maxX - bounds.minX + 1,
      bounds.maxY - bounds.minY + 1,
      bounds.maxZ - bounds.minZ + 1,
    );
    this.massPreview.visible = true;
  }

  private buildHollowCuboid(from: Anchor, to: Anchor): void {
    const bounds = cuboidBounds(from, to);
    if (hollowShellCount(bounds) > MAX_BATCH_EDITS) {
      showToast(`Mass build is limited to ${MAX_BATCH_EDITS} blocks`);
      return;
    }
    const shell = collectShell(bounds);
    const changes: CellChange[] = [];
    for (const [x, y, z] of shell) {
      if (!this.world.canEditAt(x, z)) {
        showToast('The whole mass build must stay inside your zone');
        return;
      }
      if (this.intersectsPlayer(x, y, z)) {
        showToast('Mass build would intersect your character');
        return;
      }
      if (this.world.getBlock(x, y - 1, z) === CRAFT_BLOCK_TYPE) {
        showToast('The cell above a craft block is reserved for its voxel editor');
        return;
      }
      const before = this.world.getBlock(x, y, z);
      const beforeTransform = this.world.getBlockTransform(x, y, z);
      if (before !== from.type || beforeTransform !== from.transform) {
        changes.push({
          x,
          y,
          z,
          before,
          after: from.type,
          beforeTransform,
          afterTransform: from.transform,
          beforeViewId: this.world.getBlockViewId(x, y, z),
          afterViewId: this.world.pickPlacementViewId(from.type),
          beforeAnimOrigin: this.world.getBlockAnimOrigin(x, y, z),
          afterAnimOrigin: currentWorldAnimTick(),
        });
      }
    }
    this.clearAnchor();
    this.massPreview.visible = false;
    if (changes.length === 0) return;
    this.perform({ changes });
  }

  /** Hollow shell replace: only changes existing (non-air) blocks. */
  private replaceHollowCuboid(from: Anchor, to: Anchor): void {
    const bounds = cuboidBounds(from, to);
    if (hollowShellCount(bounds) > MAX_BATCH_EDITS) {
      showToast(`Mass replace is limited to ${MAX_BATCH_EDITS} blocks`);
      return;
    }
    const shell = collectShell(bounds);
    const changes: CellChange[] = [];
    for (const [x, y, z] of shell) {
      if (!this.world.canEditAt(x, z)) {
        showToast('The whole mass replace must stay inside your zone');
        return;
      }
      if (this.world.getBlock(x, y - 1, z) === CRAFT_BLOCK_TYPE) {
        showToast('The cell above a craft block is reserved for its voxel editor');
        return;
      }
      const before = this.world.getBlock(x, y, z);
      if (before === BLOCK_AIR) continue;
      const beforeTransform = this.world.getBlockTransform(x, y, z);
      if (before === from.type && beforeTransform === from.transform) continue;
      changes.push({
        x,
        y,
        z,
        before,
        after: from.type,
        beforeTransform,
        afterTransform: from.transform,
        beforeViewId: this.world.getBlockViewId(x, y, z),
        afterViewId: this.world.pickPlacementViewId(from.type),
        beforeAnimOrigin: this.world.getBlockAnimOrigin(x, y, z),
        afterAnimOrigin: currentWorldAnimTick(),
      });
    }
    this.clearAnchor();
    this.massPreview.visible = false;
    if (changes.length === 0) return;
    this.perform({ changes });
  }

  private validReplaceTarget(target: Anchor, toast = true): boolean {
    if (
      target.x < 0 ||
      target.x >= WORLD_SIZE_X ||
      target.z < 0 ||
      target.z >= WORLD_SIZE_Z ||
      target.y < WORLD_MIN_Y ||
      target.y >= WORLD_HEIGHT
    ) {
      return false;
    }
    if (!this.world.canEditAt(target.x, target.z)) {
      if (toast) showToast('You can build only inside your own zone');
      return false;
    }
    if (this.world.getBlock(target.x, target.y - 1, target.z) === CRAFT_BLOCK_TYPE) {
      if (toast) showToast('Use micro-voxels in the cell above a craft block');
      return false;
    }
    if (target.type === CRAFT_BLOCK_TYPE && target.y + 1 >= WORLD_HEIGHT) {
      if (toast) showToast('Craft block needs one free editor cell above it');
      return false;
    }
    return true;
  }

  private clearAnchor(): void {
    this.anchor = null;
    this.anchorKind = null;
  }

  private perform(action: BuildAction): void {
    this.applyAction(action, true);
    this.undoStack.push(action);
    if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  private applyAction(action: BuildAction, forward: boolean): void {
    const edits: BlockEditTuple[] = [];
    for (const change of action.changes) {
      const type = forward ? change.after : change.before;
      const transform = forward ? change.afterTransform : change.beforeTransform;
      const viewId = forward ? change.afterViewId : change.beforeViewId;
      const animOrigin = forward ? change.afterAnimOrigin : change.beforeAnimOrigin;
      this.world.setBlockState(
        change.x, change.y, change.z, type, transform, viewId, animOrigin,
      );
      edits.push([change.x, change.y, change.z, type, transform, viewId, animOrigin]);
    }
    if (edits.length > 0) this.events.onBatch(edits);
  }

  private validPlacement(target: Anchor, toast = true): boolean {
    if (
      target.x < 0 ||
      target.x >= WORLD_SIZE_X ||
      target.z < 0 ||
      target.z >= WORLD_SIZE_Z ||
      target.y < WORLD_MIN_Y ||
      target.y >= WORLD_HEIGHT
    ) {
      return false;
    }
    if (target.type === CRAFT_BLOCK_TYPE && target.y + 1 >= WORLD_HEIGHT) {
      if (toast) showToast('Craft block needs one free editor cell above it');
      return false;
    }
    if (!this.world.canEditAt(target.x, target.z)) {
      if (toast) showToast('You can build only inside your own zone');
      return false;
    }
    if (this.world.getBlock(target.x, target.y - 1, target.z) === CRAFT_BLOCK_TYPE) {
      if (toast) showToast('Use micro-voxels in the cell above a craft block');
      return false;
    }
    return true;
  }

  private intersectsPlayer(bx: number, by: number, bz: number): boolean {
    const p = this.player.position;
    return (
      bx + 1 > p.x - PLAYER_HALF_WIDTH &&
      bx < p.x + PLAYER_HALF_WIDTH &&
      by + 1 > p.y &&
      by < p.y + PLAYER_HEIGHT &&
      bz + 1 > p.z - PLAYER_HALF_WIDTH &&
      bz < p.z + PLAYER_HALF_WIDTH
    );
  }
}

function isTransformableBlock(type: number): boolean {
  return customBlockId(type) !== null || libraryBlockId(type) !== null;
}

interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

function placementCell(hit: VoxelHit, type: number): Anchor {
  return {
    x: hit.x + hit.nx,
    y: hit.y + hit.ny,
    z: hit.z + hit.nz,
    type,
    transform: 0,
  };
}

function hitCell(hit: VoxelHit, type: number): Anchor {
  return {
    x: hit.x,
    y: hit.y,
    z: hit.z,
    type,
    transform: 0,
  };
}

function cuboidBounds(a: Anchor, b: Anchor): Bounds {
  return {
    minX: Math.min(a.x, b.x),
    maxX: Math.max(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxY: Math.max(a.y, b.y),
    minZ: Math.min(a.z, b.z),
    maxZ: Math.max(a.z, b.z),
  };
}

function hollowShellCount(b: Bounds): number {
  const x = b.maxX - b.minX + 1;
  const y = b.maxY - b.minY + 1;
  const z = b.maxZ - b.minZ + 1;
  return x * y * z - Math.max(0, x - 2) * Math.max(0, y - 2) * Math.max(0, z - 2);
}

function collectShell(b: Bounds): Array<[number, number, number]> {
  const cells = new Map<string, [number, number, number]>();
  const add = (x: number, y: number, z: number) => {
    cells.set(`${x},${y},${z}`, [x, y, z]);
  };

  for (let x = b.minX; x <= b.maxX; x++) {
    for (let z = b.minZ; z <= b.maxZ; z++) {
      add(x, b.minY, z);
      add(x, b.maxY, z);
    }
  }
  for (let y = b.minY; y <= b.maxY; y++) {
    for (let z = b.minZ; z <= b.maxZ; z++) {
      add(b.minX, y, z);
      add(b.maxX, y, z);
    }
    for (let x = b.minX; x <= b.maxX; x++) {
      add(x, y, b.minZ);
      add(x, y, b.maxZ);
    }
  }
  return [...cells.values()];
}

function sameCell(a: VoxelHit, b: VoxelHit | null): boolean {
  return !!b && a.x === b.x && a.y === b.y && a.z === b.z;
}

function samePosition(a: Anchor, b: Anchor): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z;
}

